import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { AppPaginationOptions, BaseQueryParams } from '@/common/interfaces';

import { ChangeMainDomainProcessStatus, DomainRegluingsQueryKeys, SiteDomainsGatewayKeys, SiteDomainsQueryKeys, useGetByIdSiteDomainsQuery } from '@/modules/Domains';
import { SitesQueryKeys, useGetContentByIdSitesQuery } from '@/modules/Sites';

type Data = {
  siteId: string;
  status: ChangeMainDomainProcessStatus;
  errorMessage?: string;
  newMainDomainId?: string;
}

interface Args {
  siteId: string;
  domainId?: string | null;
  pageParam?: AppPaginationOptions | null;
}

export const useChangeMainDomainProcessStatus = ({ siteId, domainId, pageParam }: Args) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const isAllSiteDomainsRequestSuccess = useIsSuccessful({ queryKey: [SiteDomainsQueryKeys.GetAllDomains, siteId, pageParam] });
  const isSiteRequestSuccess = useIsSuccessful({ queryKey: [SitesQueryKeys.GetContentById, siteId] });

  useAppSocket<Data>(SiteDomainsGatewayKeys.UpdateMainChangingProcessStatus, async ({ lastMessage }) => {
    const { siteId: id, status, newMainDomainId, errorMessage } = lastMessage;

    if (siteId === id) {
      if (status === ChangeMainDomainProcessStatus.None) {
        if (isAllSiteDomainsRequestSuccess) {
          await queryClient.refetchQueries([SiteDomainsQueryKeys.GetAllDomains, siteId, pageParam]);
        }

        if (domainId) {
          queryClient.setQueryData<
            BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']
          >([SiteDomainsQueryKeys.GetByIdDomains, id, domainId], (data) => {
            return {
              ...data,
              isSitesMain: domainId === newMainDomainId,
            } as BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data'];
          });

          await queryClient.refetchQueries([SiteDomainsQueryKeys.GetByIdDomains, id, domainId], { exact: true });

          await queryClient.refetchQueries([DomainRegluingsQueryKeys.GetAllRegluings, id, domainId], { exact: true });

          appMessageApi.success('Основний домен сайту змінено успішно!');
        }
      }

      if (status === ChangeMainDomainProcessStatus.Failed) {
        appMessageApi.error(`Помилка при зміні основного домену сайту! ${ errorMessage || '' }`);
      }

      if (isSiteRequestSuccess) {
        queryClient.setQueryData<
        BaseQueryParams<typeof useGetContentByIdSitesQuery>['data']
        >([SitesQueryKeys.GetContentById, id], (data) => {
          return {
            ...data,
            changeMainDomainProcessStatus: status
          } as BaseQueryParams<typeof useGetContentByIdSitesQuery>['data'];
        });
      }
    }
  });
};
